import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import A4Sheet from "@/components/A4Sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { UploadSimple, Warning, Sparkle, FilePdf, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OWi() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [zusatz, setZusatz] = useState("");
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [manual, setManual] = useState({
    mandant_name: "",
    behoerde: "", aktenzeichen_behoerde: "", tatvorwurf: "",
    tatdatum: "", bussgeld: 0, punkte: 0, bescheid_datum: "",
  });

  const load = useCallback(async () => {
    const r = await api.get("/owi");
    setRows(r.data);
    if (selected) {
      const u = r.data.find(x => x.id === selected.id);
      if (u) setSelected(u);
    }
  }, [selected]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uploadFile = async (file) => {
    setUploading(true);
    const tId = toast.loading("Bußgeldbescheid wird analysiert...");
    try {
      // 1. Upload file
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kategorie", "bussgeld");
      const up = await api.post("/dokumente/upload", fd);
      // 2. Analyze
      const an = await api.post(`/dokumente/${up.data.id}/analyze`);
      const d = an.data.extracted_data || {};
      // 3. Create OWi from extracted data
      const owi = await api.post("/owi", {
        mandant_name: d.betroffener_name || "",
        data: {
          behoerde: d.behoerde,
          aktenzeichen_behoerde: d.aktenzeichen_behoerde,
          tatvorwurf: d.tatvorwurf,
          tatdatum: d.tatdatum,
          tatort: d.tatort,
          geschwindigkeit_gemessen: d.geschwindigkeit_gemessen,
          geschwindigkeit_erlaubt: d.geschwindigkeit_erlaubt,
          bussgeld: d.bussgeld,
          punkte: d.punkte,
          fahrverbot_monate: d.fahrverbot_monate,
          bescheid_datum: d.bescheid_datum,
          einspruchsfrist: d.einspruchsfrist,
        },
      });
      toast.dismiss(tId);
      toast.success("OWi-Akte angelegt");
      setSelected(owi.data);
      load();
    } catch (err) {
      toast.dismiss(tId);
      toast.error(err?.response?.data?.detail || "Upload/Analyse fehlgeschlagen");
    } finally { setUploading(false); }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) await uploadFile(f);
  };

  const generateEinspruch = async () => {
    if (!selected) return;
    setGenerating(true);
    const tId = toast.loading("Einspruch wird generiert...");
    try {
      const r = await api.post(`/owi/${selected.id}/einspruch`, { zusatz_info: zusatz });
      toast.dismiss(tId);
      toast.success("Einspruch generiert");
      setSelected(s => ({ ...s, einspruch_content: r.data.content, status: "einspruch_erhoben" }));
      setZusatz("");
      load();
    } catch (err) {
      toast.dismiss(tId);
      toast.error(errMsg(err, "Einspruch konnte nicht erstellt werden"), { duration: 8000 });
    } finally { setGenerating(false); }
  };

  const createManual = async (e) => {
    e.preventDefault();
    const { mandant_name, ...data } = manual;
    await api.post("/owi", { mandant_name, data });
    toast.success("OWi-Akte angelegt");
    setCreateOpen(false);
    setManual({ mandant_name: "", behoerde: "", aktenzeichen_behoerde: "", tatvorwurf: "", tatdatum: "", bussgeld: 0, punkte: 0, bescheid_datum: "" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("OWi-Akte löschen?")) return;
    await api.delete(`/owi/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  return (
    <div className="space-y-8" data-testid="owi-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2">OWi-Verfahren</p>
          <h1 className="font-serif text-4xl tracking-tight">Bußgeldbescheide</h1>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="new-owi-manual-btn"><Plus size={16} className="mr-2" /> Manuell anlegen</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif text-2xl">OWi manuell anlegen</DialogTitle></DialogHeader>
            <form onSubmit={createManual} className="space-y-3">
              <Input placeholder="Mandant" value={manual.mandant_name} onChange={e => setManual(m => ({ ...m, mandant_name: e.target.value }))} data-testid="owi-mandant" />
              <Input placeholder="Behörde" value={manual.behoerde} onChange={e => setManual(m => ({ ...m, behoerde: e.target.value }))} data-testid="owi-behoerde" />
              <Input placeholder="Aktenzeichen Behörde" value={manual.aktenzeichen_behoerde} onChange={e => setManual(m => ({ ...m, aktenzeichen_behoerde: e.target.value }))} data-testid="owi-az" />
              <Input placeholder="Tatvorwurf" value={manual.tatvorwurf} onChange={e => setManual(m => ({ ...m, tatvorwurf: e.target.value }))} data-testid="owi-tat" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="date" value={manual.tatdatum} onChange={e => setManual(m => ({ ...m, tatdatum: e.target.value }))} data-testid="owi-tatdatum" />
                <Input type="date" value={manual.bescheid_datum} onChange={e => setManual(m => ({ ...m, bescheid_datum: e.target.value }))} data-testid="owi-bescheiddatum" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Bußgeld €" value={manual.bussgeld} onChange={e => setManual(m => ({ ...m, bussgeld: parseFloat(e.target.value) || 0 }))} data-testid="owi-bussgeld" />
                <Input type="number" placeholder="Punkte" value={manual.punkte} onChange={e => setManual(m => ({ ...m, punkte: parseInt(e.target.value) || 0 }))} data-testid="owi-punkte" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="owi-submit">Anlegen</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`dropzone ${dragOver ? "dropzone--active" : ""} rounded-lg p-10 text-center`}
        data-testid="owi-dropzone"
      >
        <UploadSimple size={36} weight="duotone" className="mx-auto text-[#1A2238]" />
        <p className="font-serif text-2xl mt-3">Bußgeldbescheid hochladen</p>
        <p className="text-sm text-[#4B5563] mt-1">Tatvorwurf, Behörde und Fristen werden automatisch erkannt</p>
        <label className="inline-block mt-4">
          <input type="file" accept="application/pdf,image/*" className="hidden" disabled={uploading} onChange={e => {
            const f = e.target.files?.[0];
            if (f) uploadFile(f);
          }} data-testid="owi-file-input" />
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1A2238] text-white text-sm cursor-pointer hover:bg-[#2C3859]">
            <UploadSimple size={16} /> {uploading ? "Lädt..." : "Datei auswählen"}
          </span>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-2">
          <h3 className="font-serif text-2xl mb-2">Verfahren</h3>
          {rows.length === 0 ? (
            <p className="text-sm text-[#4B5563]">Noch keine OWi-Verfahren</p>
          ) : rows.map(r => {
            const fristStr = r.data?.einspruchsfrist;
            let fristBadge = null;
            if (fristStr) {
              try {
                const f = new Date(fristStr);
                const now = new Date();
                const days = Math.ceil((f - now) / 86400000);
                let cls = "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30";
                let txt = `noch ${days} Tage`;
                if (days < 0) { cls = "bg-[#D32F2F]/10 text-[#B91C1C] border-[#D32F2F]/30"; txt = `${Math.abs(days)} Tage überzogen!`; }
                else if (days === 0) { cls = "bg-[#D32F2F]/10 text-[#B91C1C] border-[#D32F2F]/30 animate-pulse"; txt = "heute!"; }
                else if (days <= 3) { cls = "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30"; txt = `noch ${days} Tag${days === 1 ? "" : "e"}`; }
                fristBadge = <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{txt}</span>;
              } catch { /* ignore */ }
            }
            return (
              <button
                key={r.id}
                onClick={() => { setSelected(r); setZusatz(""); }}
                className={`w-full text-left bg-white border rounded-lg p-4 transition-all hover:shadow-sm ${selected?.id === r.id ? "border-[#1A2238] ring-1 ring-[#1A2238]" : "border-[#E5E7EB]"}`}
                data-testid={`owi-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{r.mandant_name || "—"}</p>
                    <p className="text-xs text-[#4B5563] mt-1 truncate">{r.data?.tatvorwurf || "Tatvorwurf?"}</p>
                    {r.data?.bussgeld ? <p className="font-mono text-sm mt-1">{new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(r.data.bussgeld)}</p> : null}
                    {fristBadge && <div className="mt-2">{fristBadge}</div>}
                  </div>
                  <Warning size={18} weight="duotone" className="text-[#D32F2F] shrink-0" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
              <FilePdf size={40} weight="duotone" className="mx-auto text-[#9CA3AF]" />
              <p className="font-serif text-2xl mt-4">Verfahren auswählen</p>
              <p className="text-sm text-[#4B5563]">Wählen Sie links ein Verfahren oder laden Sie einen Bescheid hoch.</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="owi-detail">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-serif text-2xl">{selected.mandant_name || "OWi-Verfahren"}</h3>
                  <Button variant="ghost" size="sm" onClick={() => del(selected.id)} data-testid="owi-delete-btn"><Trash size={14} /></Button>
                </div>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <Detail k="Behörde" v={selected.data?.behoerde} />
                  <Detail k="Aktenzeichen" v={selected.data?.aktenzeichen_behoerde} />
                  <Detail k="Tatvorwurf" v={selected.data?.tatvorwurf} />
                  <Detail k="Tatdatum" v={selected.data?.tatdatum} />
                  <Detail k="Tatort" v={selected.data?.tatort} />
                  <Detail k="Bußgeld" v={selected.data?.bussgeld ? `${selected.data.bussgeld} €` : null} />
                  <Detail k="Punkte" v={selected.data?.punkte} />
                  <Detail k="Fahrverbot (Monate)" v={selected.data?.fahrverbot_monate} />
                  <Detail k="Bescheid-Datum" v={selected.data?.bescheid_datum} />
                  <Detail k="Einspruchsfrist" v={selected.data?.einspruchsfrist} highlight />
                </dl>
              </div>

              <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
                <h3 className="font-serif text-2xl mb-4">Einspruch generieren</h3>
                <Textarea rows={3} placeholder="Zusätzliche Hinweise (optional)" value={zusatz} onChange={e => setZusatz(e.target.value)} data-testid="owi-zusatz" />
                <Button onClick={generateEinspruch} disabled={generating} className="mt-3 bg-[#1A2238] hover:bg-[#2C3859]" data-testid="owi-generate-btn">
                  <Sparkle size={16} className="mr-2" /> {generating ? "Erstellt..." : "Einspruch generieren"}
                </Button>
              </div>

              {selected.einspruch_content && (
                <div data-testid="owi-einspruch-content">
                  <p className="overline text-[#D32F2F] mb-3">Einspruchsschreiben</p>
                  <A4Sheet
                    content={selected.einspruch_content}
                    user={user}
                    typ="owi_einspruch"
                    createdAt={selected.created_at}
                    meta={{
                      aktenzeichen: selected.data?.aktenzeichen_behoerde,
                      empfaenger: selected.data?.behoerde
                        ? `${selected.data.behoerde}\n– Bußgeldstelle –`
                        : null,
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ k, v, highlight }) {
  return (
    <div>
      <dt className="overline text-[#9CA3AF]">{k}</dt>
      <dd className={`font-mono ${highlight ? "text-[#D32F2F] font-semibold" : "text-[#0A192F]"}`}>{v || "—"}</dd>
    </div>
  );
}
